import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import useApplicationStore from './store/applicationStore';
import Header from './components/Header';
import Footer from './components/Footer';
import Homepage from './components/homepage/Homepage';
import EmiPage from './components/emiPage/EmiPage';
import PrivacyPolicyPage from './components/privacyPolicy/PrivacyPolicyPage';
import LenderPage from './components/lender/LenderPage';
import LendingPartnerPage from './components/lendingPartnerPage/LendingPartnerPage';
import EligibilityPage from './components/eligibilityPage/EligibilityPage';
import ApplicationJourneyPage from './components/applicationJourney/ApplicationJourneyPage';
import ThankYouPage from './components/applicationJourney/ThankYouPage';

// Component to handle navigation restoration and capture UTM params
const NavigationRestorer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setUtmData = useApplicationStore(state => state.setUtmData);
  const resetUtm = useApplicationStore(state => state.resetUtm);
  const utm = useApplicationStore(state => state.utm);
  const utmPersisted = useApplicationStore(state => state.utmPersisted);

  useEffect(() => {
    // Clear any old stored page info when app loads
    // We want users to naturally land on homepage when returning from lender pages
    const storedPageInfo = localStorage.getItem('snapfunds_previous_page');
    if (storedPageInfo) {
      try {
        const pageInfo = JSON.parse(storedPageInfo);
        const timeDiff = Date.now() - pageInfo.timestamp;

        // Clear stored info if it's old (more than 1 hour)
        if (timeDiff >= 3600000) {
          localStorage.removeItem('snapfunds_previous_page');
        }
      } catch (error) {
        console.error('Error parsing stored page info:', error);
        localStorage.removeItem('snapfunds_previous_page');
      }
    }

    // Only initialize UTM capture/reset on the first app load in this browser tab/session.
    // We use sessionStorage to detect first-run per tab so internal SPA navigation doesn't reset state.
    try {
      // Build a lowercase-key map of params so incoming param *names* are treated case-insensitively.
      const rawParams = new URLSearchParams(location.search);
      const lowerParams = new Map();
      for (const [k, v] of rawParams.entries()) {
        lowerParams.set(k.toLowerCase(), v);
      }

      // Accept either camelCase, snake_case or any case variations by reading lowercase keys.
      const utmSourceRaw = lowerParams.get('utmsource') || '';
      const utmMediumRaw = lowerParams.get('utmmedium') || '';
      const utmCampaignRaw = lowerParams.get('utmcampaign') || '';

      const utmSource = utmSourceRaw ? utmSourceRaw.toUpperCase() : '';
      const utmMedium = utmMediumRaw ? utmMediumRaw.toUpperCase() : '';
      const utmCampaign = utmCampaignRaw ? utmCampaignRaw.toUpperCase() : '';
      const hasParams = !!(utmSource || utmMedium || utmCampaign);

      const initialized = sessionStorage.getItem('snapfunds_utm_initialized');
      // If session already initialized with utm, nothing to do.
      if (initialized === 'with_utm' && !hasParams) return;
      // If session was initialized without utm and there are no params now, nothing to do.
      if (initialized === 'without_utm' && !hasParams) return;

      if (hasParams) {
        const utmPayload = {};
        if (utmSource) utmPayload.utmSource = utmSource;
        if (utmMedium) utmPayload.utmMedium = utmMedium;
        if (utmCampaign) utmPayload.utmCampaign = utmCampaign;
        setUtmData(utmPayload);
        // mark session as initialized with utm so we persist/append across navigation
        sessionStorage.setItem('snapfunds_utm_initialized', 'with_utm');
        try { console.debug('UTM captured:', utmPayload); } catch (e) {}
      } else {
        // No relevant UTM on initial load — clear any previously persisted UTM so stale values are not reused.
        resetUtm();
        sessionStorage.setItem('snapfunds_utm_initialized', 'without_utm');
      }
    } catch (err) {
      // ignore malformed URLs and sessionStorage errors
    }
  }, [navigate, location.pathname, location.search, setUtmData, resetUtm]);

  // If UTM has been captured previously (utmPersisted) then ensure internal navigation URLs
  // display the utm params so they follow the user across pages. We append params only
  // when they are not already present in the current URL.
  useEffect(() => {
    try {
      if (!utmPersisted) return;

      // Treat URL param *names* case-insensitively when checking presence.
      const currentRawParams = new URLSearchParams(location.search);
      const currentLowerParams = new Map();
      for (const [k, v] of currentRawParams.entries()) {
        currentLowerParams.set(k.toLowerCase(), v);
      }
      const hasUtmInUrl = currentLowerParams.has('utmsource') && currentLowerParams.has('utmmedium') && currentLowerParams.has('utmcampaign');

      if (!hasUtmInUrl) {
        const newParams = new URLSearchParams(location.search);
        // set both snake_case (marketing standard) and camelCase (internal) keys so downstream code picks them up
        if (utm?.utmSource) {
          newParams.set('utmSource', utm.utmSource);
        }
        if (utm?.utmMedium) {
          newParams.set('utmMedium', utm.utmMedium);
        }
        if (utm?.utmCampaign) {
          newParams.set('utmCampaign', utm.utmCampaign);
        }
        const newQuery = newParams.toString();
        const newUrl = location.pathname + (newQuery ? `?${newQuery}` : '') + (location.hash || '');
        // replaceState avoids adding history entries and avoids full navigation
        window.history.replaceState(null, '', newUrl);
      }
    } catch (err) {
      // ignore
    }
  }, [location.pathname, location.search, utmPersisted, utm]);

  return null;
};

function App() {
  return (
    <Router>
      <NavigationRestorer />
      <Header />
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/emi-calculator" element={<EmiPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage/>}/>
        <Route path="/lending-partner/:lenderName" element={<LenderPage />} />
        <Route path="/lending-partner" element={<LendingPartnerPage />} />
        <Route path="/application/check-eligibility" element={<EligibilityPage />} />
        <Route path="/application/thank-you" element={<ThankYouPage />} />
        <Route path="/application/journey" element={<ApplicationJourneyPage />} />
        <Route path="/application/journey#personaldetails" element={<ApplicationJourneyPage />} />
        <Route path="/application/journey#professionaldetails" element={<ApplicationJourneyPage />} />
        <Route path="/application/journey#documents" element={<ApplicationJourneyPage />} />
      </Routes>
      <Footer />
    </Router>
  );
}

export default App;